import React from 'react'
import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { fmt } from './columnasPrecios'
import {
  claveCelda, etiquetaMoto, faltaMargen, pctActual, pvpEfectivoDe, leerPct, esPctValido,
} from './margenesMasivo'

/**
 * Los márgenes de VARIAS motos a la vez: una fila por moto, una columna por clasificación.
 *
 * Es la versión en grilla de MargenTarjetas, para cuando se arma el plano de varias motos juntas y
 * poner el % tarjeta por tarjeta, moto por moto, sería repetir la misma decisión N veces. Mismos
 * colores: rojo = no resuelve ningún margen, verde = cambiado y sin guardar.
 *
 * Como en las tarjetas, lo tecleado no vive acá: sube al padre, que es el que sabe guardar y
 * recalcular. La grilla ya viene armada (construirGrilla).
 */
const thBase = (extra = {}) => ({
  padding: '7px 9px',
  fontSize: 10.5,
  fontWeight: 700,
  color: '#56606b',
  background: '#f4f6f8',
  borderBottom: '1px solid #e3e7ec',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
  ...extra,
})

const tdBase = {
  padding: '5px 9px',
  fontSize: 12,
  color: '#2b3440',
  borderBottom: '1px solid #eef1f4',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
}

const MargenGrilla = ({
  grilla,
  edits = {},
  onEditChange,
  aplicar = {},
  onAplicarChange,
  onAplicar,
  soloVacias = false,
  onSoloVacias,
  disabled = false,
}) => {
  const { columnas, filas } = grilla

  if (!filas.length) return null
  if (!columnas.length) {
    return (
      <div style={{ fontSize: 12.5, color: '#6b7684' }}>
        Ninguna de estas motos tiene condiciones con margen que poner.
      </div>
    )
  }

  const cabecera = (c) => {
    const estilo = thBase({ borderLeft: '1px solid #e9edf1', minWidth: 128 })
    return (
      <th key={c.lista_id} style={estilo} title={`${c.tabla_sap} · ${c.condicion}`}>
        {c.lista_label}
        <div style={{ fontWeight: 500, color: '#8a94a0', textTransform: 'none' }}>
          {c.tabla_sap} · {c.condicion}
        </div>
      </th>
    )
  }

  const celda = (f, c) => {
    const m = f.celdas[c.lista_id]
    if (!m) {
      return (
        <td
          key={c.lista_id}
          style={{ ...tdBase, background: '#fafbfc', color: '#9aa5b1', textAlign: 'right' }}
        >
          no aplica
        </td>
      )
    }
    const clave = claveCelda(f.grupo.id, c.lista_id)
    const tocado = edits[clave] !== undefined
    const actual = pctActual(m)
    const falta = faltaMargen(m)
    const num = tocado ? leerPct(edits[clave]) : null
    const invalido = tocado && num !== null && !esPctValido(num)
    const fondo = tocado ? '#f2fbf5' : (falta ? '#fdecec' : '#fff')
    return (
      <td key={c.lista_id} style={{ ...tdBase, background: fondo, borderLeft: '1px solid #eef1f4' }}>
        <TextField
          size="small"
          disabled={disabled}
          value={tocado ? edits[clave] : (actual === null ? '' : String(actual))}
          onChange={(e) => onEditChange(clave, e.target.value)}
          placeholder={falta ? 'sin margen' : ''}
          error={invalido || (falta && !tocado)}
          InputProps={{ endAdornment: <span style={{ fontSize: 12, color: '#6b7684' }}>%</span> }}
          sx={{
            width: 104,
            background: '#fff',
            '& input': {
              padding: '5px 7px',
              fontSize: 13,
              fontWeight: 700,
              color: tocado ? '#166534' : (falta ? '#8f2d2d' : '#0f2439'),
            },
          }}
        />
        <div style={{ fontSize: 10, color: tocado ? '#166534' : (falta ? '#d64545' : '#8a94a0'), marginTop: 2 }}>
          {tocado ? 'sin guardar' : (falta ? 'sin regla' : (Number(m.regla_propia_id || 0) > 0 ? 'propio' : m.origen))}
        </div>
      </td>
    )
  }

  const estMoto = thBase({ position: 'sticky', left: 0, zIndex: 2, minWidth: 210 })
  const estAplicar = thBase({ position: 'sticky', left: 0, zIndex: 2, background: '#eef4fb' })

  return (
    <div>
      <FormControlLabel
        sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontSize: 12.5, color: '#6b7684' } }}
        control={(
          <Checkbox
            size="small"
            disabled={disabled}
            checked={soloVacias}
            onChange={(e) => onSoloVacias(e.target.checked)}
          />
        )}
        label="Solo donde está vacío"
      />
      <div style={{ overflowX: 'auto', border: '1px solid #e3e7ec', borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={estMoto}>Moto</th>
              {columnas.map(cabecera)}
            </tr>
            <tr>
              <th style={estAplicar}>
                Aplicar a todas
              </th>
              {columnas.map((c) => (
                <th
                  key={c.lista_id}
                  style={{
                    padding: '5px 9px', background: '#eef4fb', borderBottom: '1px solid #e3e7ec',
                    borderLeft: '1px solid #e9edf1', whiteSpace: 'nowrap',
                  }}
                >
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      value={aplicar[c.lista_id] ?? ''}
                      onChange={(e) => onAplicarChange(c.lista_id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !disabled) onAplicar(c.lista_id)
                      }}
                      InputProps={{ endAdornment: <span style={{ fontSize: 12, color: '#6b7684' }}>%</span> }}
                      sx={{ width: 78, background: '#fff', '& input': { padding: '5px 7px', fontSize: 12.5 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={disabled}
                      sx={{ minWidth: 0, px: 1, fontSize: 11 }}
                      onClick={() => onAplicar(c.lista_id)}
                    >
                      Aplicar
                    </Button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => {
              const pvp = pvpEfectivoDe(f.grupo)
              return (
                <tr key={f.grupo.id} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                  <td
                    style={{
                      ...tdBase, position: 'sticky', left: 0, zIndex: 1, background: i % 2 ? '#fafbfc' : '#fff',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: '#0f2439' }}>{etiquetaMoto(f.grupo)}</div>
                    <div style={{ fontSize: 10.5, color: '#8a94a0' }}>
                      {f.grupo.conca} · {pvp > 0 ? fmt(pvp) : <span style={{ color: '#d64545' }}>sin PVP</span>}
                      {f.grupo.fueraDeTrade ? ' · sin propuesta de Trade' : ''}
                    </div>
                  </td>
                  {columnas.map((c) => celda(f, c))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default MargenGrilla
